import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, IconButton, Divider,
} from '@mui/material';
import { Save, ArrowBack, Add, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

interface BlendLine {
  grainType: string;
  grainVariety: string;
  siloId: string;
  siloCode: string;
  weightPercent: string;
}

const GristRecipeForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id && !window.location.pathname.endsWith('/new');

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [silos, setSilos] = useState<any[]>([]);

  const [form, setForm] = useState({
    recipeName: '',
    description: '',
    targetProteinMin: '',
    targetProteinMax: '',
    targetMoistureMax: '',
    targetAshMax: '',
    notes: '',
  });

  const [lines, setLines] = useState<BlendLine[]>([
    { grainType: 'wheat', grainVariety: '', siloId: '', siloCode: '', weightPercent: '' },
  ]);

  const totalPercent = lines.reduce((s, l) => s + (Number(l.weightPercent) || 0), 0);
  const isValid100 = Math.abs(totalPercent - 100) < 0.01;

  useEffect(() => {
    millingApi.getSilos().then((data: any) => setSilos(Array.isArray(data) ? data : [])).catch(() => {});
    if (isEdit && id) {
      millingApi.getGristRecipe(id).then((r: any) => {
        setForm({
          recipeName: r.recipeName || '',
          description: r.description || '',
          targetProteinMin: r.targetProteinMin != null ? String(r.targetProteinMin) : '',
          targetProteinMax: r.targetProteinMax != null ? String(r.targetProteinMax) : '',
          targetMoistureMax: r.targetMoistureMax != null ? String(r.targetMoistureMax) : '',
          targetAshMax: r.targetAshMax != null ? String(r.targetAshMax) : '',
          notes: r.notes || '',
        });
        setLines((r.lines || []).map((l: any) => ({
          grainType: l.grainType,
          grainVariety: l.grainVariety || '',
          siloId: l.siloId || '',
          siloCode: l.siloCode || '',
          weightPercent: String(l.weightPercent),
        })));
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id, isEdit]);

  const addLine = () => setLines(l => [...l, { grainType: 'wheat', grainVariety: '', siloId: '', siloCode: '', weightPercent: '' }]);
  const removeLine = (i: number) => setLines(l => l.filter((_, idx) => idx !== i));
  const setLine = (i: number, field: keyof BlendLine, value: string) =>
    setLines(l => l.map((line, idx) => idx === i ? { ...line, [field]: value } : line));

  const WHEAT_VARIETIES = ['hrw', 'hrs', 'srw', 'hw', 'sw', 'dur'];
  const MAIZE_VARIETIES = ['wm', 'ym', 'wx'];

  const handleSave = async () => {
    if (!form.recipeName) { setError('Recipe name is required'); return; }
    if (lines.length === 0) { setError('At least one blend line is required'); return; }
    if (!isValid100) { setError(`Blend percentages must sum to 100% (current: ${totalPercent.toFixed(2)}%)`); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        recipeName: form.recipeName,
        description: form.description || undefined,
        targetProteinMin: form.targetProteinMin !== '' ? Number(form.targetProteinMin) : undefined,
        targetProteinMax: form.targetProteinMax !== '' ? Number(form.targetProteinMax) : undefined,
        targetMoistureMax: form.targetMoistureMax !== '' ? Number(form.targetMoistureMax) : undefined,
        targetAshMax: form.targetAshMax !== '' ? Number(form.targetAshMax) : undefined,
        notes: form.notes || undefined,
        lines: lines.map((l, i) => ({
          grainType: l.grainType,
          grainVariety: l.grainVariety || undefined,
          siloId: l.siloId || undefined,
          siloCode: l.siloCode || undefined,
          weightPercent: Number(l.weightPercent),
          sortOrder: i,
        })),
      };

      if (isEdit && id) {
        await millingApi.updateGristRecipe(id, payload);
      } else {
        await millingApi.createGristRecipe(payload);
      }
      navigate('/milling/grist-recipes');
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/milling/grist-recipes')}>Back</Button>
        <Typography variant="h4">{isEdit ? 'Edit Grist Recipe' : 'New Grist Recipe'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Header */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recipe Info</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Recipe Name *" value={form.recipeName} onChange={(e) => setForm(f => ({ ...f, recipeName: e.target.value }))} fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Blend Lines */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Blend Composition</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="body2"
                    color={isValid100 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    Total: {totalPercent.toFixed(2)}% {isValid100 ? '✓' : '(must = 100%)'}
                  </Typography>
                  <Button startIcon={<Add />} size="small" onClick={addLine}>Add Row</Button>
                </Box>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Grain Type</TableCell>
                    <TableCell>Variety</TableCell>
                    <TableCell>Silo</TableCell>
                    <TableCell>Weight %</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select size="small" value={line.grainType} onChange={(e) => setLine(i, 'grainType', e.target.value)} sx={{ minWidth: 100 }}>
                          <MenuItem value="wheat">Wheat</MenuItem>
                          <MenuItem value="maize">Maize</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select size="small" value={line.grainVariety} onChange={(e) => setLine(i, 'grainVariety', e.target.value)} sx={{ minWidth: 100 }} displayEmpty>
                          <MenuItem value="">Any</MenuItem>
                          {(line.grainType === 'wheat' ? WHEAT_VARIETIES : MAIZE_VARIETIES).map(v => (
                            <MenuItem key={v} value={v}>{v.toUpperCase()}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select size="small" value={line.siloId} onChange={(e) => {
                          const silo = silos.find((s: any) => s.id === e.target.value);
                          setLine(i, 'siloId', e.target.value);
                          setLine(i, 'siloCode', silo?.siloCode || '');
                        }} sx={{ minWidth: 100 }} displayEmpty>
                          <MenuItem value="">Any</MenuItem>
                          {silos.map((s: any) => (
                            <MenuItem key={s.id} value={s.id}>{s.siloCode}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={line.weightPercent}
                          onChange={(e) => setLine(i, 'weightPercent', e.target.value)}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeLine(i)} disabled={lines.length <= 1}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Target Quality */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Target Quality</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Protein Min %" type="number" value={form.targetProteinMin} onChange={(e) => setForm(f => ({ ...f, targetProteinMin: e.target.value }))} fullWidth size="small" inputProps={{ min: 0, max: 100, step: 0.1 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Protein Max %" type="number" value={form.targetProteinMax} onChange={(e) => setForm(f => ({ ...f, targetProteinMax: e.target.value }))} fullWidth size="small" inputProps={{ min: 0, max: 100, step: 0.1 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Max Moisture %" type="number" value={form.targetMoistureMax} onChange={(e) => setForm(f => ({ ...f, targetMoistureMax: e.target.value }))} fullWidth size="small" inputProps={{ min: 0, max: 100, step: 0.01 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Max Ash %" type="number" value={form.targetAshMax} onChange={(e) => setForm(f => ({ ...f, targetAshMax: e.target.value }))} fullWidth size="small" inputProps={{ min: 0, max: 10, step: 0.001 }} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <TextField label="Notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={3} size="small" />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/milling/grist-recipes')}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {isEdit ? 'Save Changes' : 'Create Recipe'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GristRecipeForm;
